import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { CartSummary } from './cartModels';

// Basic selectors
export const selectCartItems = (state: RootState) => state.cart.items;
export const selectCartIsOpen = (state: RootState) => state.cart.isOpen;
export const selectCartLoading = (state: RootState) => state.cart.loading;
export const selectCartError = (state: RootState) => state.cart.error;

// Computed selectors
export const selectCartItemCount = createSelector(
  [selectCartItems],
  (items) => items.reduce((total, item) => total + item.quantity, 0)
);

export const selectCartTotalPrice = createSelector(
  [selectCartItems],
  (items) => {
    return items.reduce((total, item) => {
      const price = typeof item.product.price === 'string' 
        ? parseFloat(item.product.price) 
        : item.product.price;
      return total + (price * item.quantity);
    }, 0);
  }
);

export const selectCartTotalSavings = createSelector(
  [selectCartItems],
  (items) => {
    return items.reduce((total, item) => {
      if (item.product.sale) {
        const currentPrice = typeof item.product.price === 'string' 
          ? parseFloat(item.product.price) 
          : item.product.price;
        const originalPrice = currentPrice * 1.2; // Assuming 20% discount
        const savings = (originalPrice - currentPrice) * item.quantity;
        return total + savings;
      }
      return total;
    }, 0);
  }
);

export const selectCartSummary = createSelector(
  [selectCartItems, selectCartTotalPrice, selectCartTotalSavings],
  (items, totalPrice, totalSavings): CartSummary => {
    const totalItems = items.reduce((total, item) => total + item.quantity, 0);
    const subtotal = totalPrice;
    const shipping = subtotal > 50 ? 0 : 4.99; // Free shipping over ₪50
    const tax = subtotal * 0.08; // 8% tax
    const total = subtotal + shipping + tax;

    return {
      totalItems,
      totalPrice,
      totalSavings,
      subtotal,
      shipping,
      tax,
      total,
    };
  }
);

export const selectCartItemById = (productId: string) => createSelector(
  [selectCartItems],
  (items) => items.find(item => item.product._id === productId)
);

export const selectIsItemInCart = (productId: string) => createSelector(
  [selectCartItems],
  (items) => items.some(item => item.product._id === productId)
);
